<template>
  <v-form ref="tradeForm" v-model="formValid" v-if="getCart().items.length > 0">
    <chrome-autofill-honeypot></chrome-autofill-honeypot>
    <v-row class="no-gutters-xs">
      <v-col cols="12" :class="{'px-2 pl-0': $vuetify.breakpoint.xsOnly, 'pr-4': !$vuetify.breakpoint.xsOnly}" md="7">

        <p class="text-subtitle-1 font-weight-bold pt-2 pb-2">Pending Trade</p>
        <p class="text-caption">
          Please note that prices are indications only and dynamically change
          with the market until the order is executed.
        </p>
        <template v-if="getSelectedTradingRole === TradingRoles.SALES_REP">
          <retail-ticket :buy-sell="buySell"></retail-ticket>
        </template>
        <template v-else>
          <template v-if="showRetailTicket()">
            <div v-for="index in [0, 1]" :key="index" class="mb-12">
              <div v-if="wholesaleTicketIndex === index">
                <h3 class="h3 mt-2 mb-2">
                  <v-btn @click="swapWholesaleRetailTicketOrder()" icon>
                    <v-icon>mdi-swap-vertical-bold</v-icon>
                  </v-btn>
                  Wholesale Ticket
                </h3>
                <wholesale-ticket
                  :buy-sell="buySell"
                  v-on:updateTotal="updateTotal"
                  v-on:go-to-pre-settlement-program="$emit('go-to-pre-settlement-program')"
                ></wholesale-ticket>
              </div>
              <template
                v-if="
                  fulfillmentType !== FulfillmentTypes.EQUITY_TRUST &&
                  wholesaleTicketIndex === index
                "
              >
                <v-checkbox
                  dense
                  v-model="generateRetailTicket"
                  label="Generate Retail Ticket"
                ></v-checkbox>
              </template>
              <div v-if="retailTicketIndex === index">
                <h3 class="h3 mt-2 mb-2">
                  <v-btn @click="swapWholesaleRetailTicketOrder()" icon>
                    <v-icon>mdi-swap-vertical-bold</v-icon>
                  </v-btn>
                  Retail Ticket
                </h3>
                <retail-ticket :buy-sell="buySell"></retail-ticket>
              </div>
            </div>
          </template>
          <template v-else>
            <wholesale-ticket
              :buy-sell="buySell"
              v-on:updateTotal="updateTotal"
              v-on:go-to-pre-settlement-program="$emit('go-to-pre-settlement-program')"
            ></wholesale-ticket>
            <template
              v-if="
                getSelectedTradingRole === TradingRoles.SALES_MANAGER &&
                fulfillmentType !== FulfillmentTypes.EQUITY_TRUST
              "
            >
              <v-checkbox
                dense
                v-model="generateRetailTicket"
                label="Generate Retail Ticket"
              ></v-checkbox>
            </template>
          </template>
        </template>
      </v-col>
      <v-col cols="12" :class="{'px-2': $vuetify.breakpoint.xsOnly, 'pl-4': !$vuetify.breakpoint.xsOnly}" md="5">

        <div v-if="hasTradeExecutionError">
          <trade-execution
            :buy-sell="buySell"
            :company-id="getSelectedCompany.id"
            :total="cartTotal"
            v-on:close="close()"
            v-on:executeTrade="executeTrade()"
            v-on:error-state="handleTradeExecutionError"
          ></trade-execution>
        </div>
        <v-stepper v-else v-model="detailsStep" class="elevation-0 pa-0 trade-stepper">
          <v-stepper-items>
            <v-stepper-content step="1" class="pt-1">
              <payment-method
                ref="paymentMethod"
                :buy-sell="buySell"
                :company-id="getSelectedCompany.id"
                v-on:loaded="requiredComponentsLoaded.paymentMethod = true"
                v-on:addBankingInfo="addBankingInfo"
              ></payment-method>
              <v-alert
                v-if="buySell === 'sell' && processingDelayMessage" 
                type="info"
                color="secondary"
                dense
                class="mt-3 mb-2 black--text"
              >
                <template v-slot:prepend>
                  <v-icon color="black">mdi-alert-circle-outline</v-icon>
                </template>
                Estimated processing delays — {{ processingDelayMessage }}
              </v-alert>
              <template
                v-if="
                  getSelectedTradingRole === TradingRoles.SALES_MANAGER &&
                  isEquityTrustEligible() &&
                  iraEligibleCart()
                "
              >
                <div class="font-weight-bold pa-0 pt-2">Fulfillment Type</div>
                <v-select
                  ref="fulfillmentTypeField"
                  dense
                  v-model="fulfillmentType"
                  :items="
                    Object.keys(FulfillmentTypes).map((type) => {
                      return {
                        id: FulfillmentTypes[type],
                        name: FulfillmentTypes[type],
                      }
                    })
                  "
                  item-text="name"
                  item-value="id"
                  label="Fulfillment Type"
                  single-line
                  :rules="fulfillmentTypeRules()"
                  @change="
                    ;(equityTrustAccountNumber = null) &&
                      (selectedShippingAddress = null) &&
                      resetEquityTrust()
                  "
                ></v-select>
              </template>
              <template
                v-if="
                  iraEligibleCart() &&
                  fulfillmentType === FulfillmentTypes.EQUITY_TRUST
                "
              >
                <div class="font-weight-bold pa-0 pt-2">
                  Equity Trust Account Number
                </div>
                <v-text-field
                  dense
                  ref="equityTrustAccountNumberField"
                  v-model.trim="equityTrustAccountNumber"
                  single-line
                  :rules="getRulesForEquityTrust()"
                  @change="resetEquityTrust()"
                  @keyup="resetEquityTrust()"
                  :hint="equityTrustLinkedAccountHint()"
                  :persistent-hint="showEquityTrustLinkedAccountHint()"
                >
                  <template v-slot:message="{ message, key }">
                    <span v-html="message"></span>
                  </template>
                  <v-btn
                    slot="append-outer"
                    small
                    @click="verifyEquityTrust()"
                    :loading="verifyEquityTrustLoading"
                    class="mt-n2"
                    v-if="!equityTrustVerified"
                  >
                    <v-icon small color="primary" class="mr-1"
                    >mdi-check-bold
                    </v-icon
                    >
                    Verify
                  </v-btn
                  >
                  <v-btn
                    v-else
                    text
                    small
                    class="mt-n2 font-italic"
                    slot="append-outer"
                    color="green"
                  >
                    <v-icon small color="green" class="mr-1"
                    >mdi-check-bold
                    </v-icon
                    >
                    Verified
                  </v-btn>
                </v-text-field>
              </template>
              <shipping-instructions
                v-if="
                  buySell === `buy` && fulfillmentType === FulfillmentTypes.STANDARD
                "
                :buy-sell="buySell"
                :company-id="getSelectedCompany.id"
                v-on:loaded="
                  requiredComponentsLoaded.shippingInstructions = true
                "
              ></shipping-instructions>
              <template
                v-if="
                  (iraEligibleCart() &&
                  fulfillmentType === FulfillmentTypes.EQUITY_TRUST)
                  || this.selectedShippingInstructionId === this.DEPOSITORY_INSTRUCTIONS_NS_ID
                "
              >
              <div class="font-weight-bold pa-0 pt-2">Choose Depository</div>
                <v-select
                  dense
                  v-model="selectedDepoAddress"
                  :items="getDepositoryLocationsWrapper()"
                  :hint="warehouseAddressHint(selectedDepoAddress)"
                  :persistent-hint="true"
                  item-text="label"
                  item-value="label"
                  label="Depository Address"
                  single-line
                  return-object
                  :rules="[...requireObjectField, requireDepoAddress]"
                >
                  <template v-slot:selection="data">
                    {{ data.item.label }}
                  </template>
                  <template v-slot:item="data">
                    {{ data.item.label }}
                  </template>
                </v-select
                >
              <v-row v-if="selectedShippingInstructionId === this.DEPOSITORY_INSTRUCTIONS_NS_ID 
                && fulfillmentType === FulfillmentTypes.STANDARD" class="mt-1">
              <v-col cols="12" sm="6">
                <v-text-field
                  dense
                  label="Account Holder Name"
                  v-model="standardDepoAttnName"
                  ref="standardDepoAttnName"
                  :rules="[stringRequired, ...depoRules()]"
                  @keyup="validateDepoFields()"
                  @change="validateDepoFields()"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  dense
                  label="Account Number"
                  v-model="standardDepoAccountNumber"
                  ref="standardDepoAccountNumber"
                  :rules="[stringRequired, ...depoRules()]"
                  @keyup="validateDepoFields()"
                  @change="validateDepoFields()"
                  v-alphanumeric
                ></v-text-field>
              </v-col>
              </v-row>
              </template>
              <shipping-address
                v-if="fulfillmentType === FulfillmentTypes.STANDARD 
                        && this.selectedShippingInstructionId !== this.DEPOSITORY_INSTRUCTIONS_NS_ID" 
                :buy-sell="buySell"
                :company-id="getSelectedCompany.id"
                :address-validator="validateShippingAddress"
                v-on:addNewAddress="addNewAddress"
                v-on:loaded="requiredComponentsLoaded.shippingAddress = true"
              ></shipping-address>
              <shipping-address
                v-if="(this.selectedShippingInstructionId === this.DEPOSITORY_INSTRUCTIONS_NS_ID && this.selectedDepoAddress.id === 'OTHER')"
                :buy-sell="buySell"
                :company-id="getSelectedCompany.id"
                :address-validator="validateShippingAddress"
                v-on:addNewAddress="addNewAddress"
                v-on:loaded="requiredComponentsLoaded.shippingAddress = true"
              ></shipping-address>
              <!-- Shipping Hold Option (Buy orders only) -->
              <template v-if="buySell === 'buy'">
                <v-checkbox
                  dense
                  v-model="shippingHoldEnabled"
                  label="Place Shipping Hold"
                  hint="Delay shipment until the selected date"
                  persistent-hint
                  class="mt-2"
                ></v-checkbox>
                <template v-if="shippingHoldEnabled">
                  <div class="font-weight-bold pa-0 pt-2">
                    Shipping Hold Release Date
                  </div>
                  <v-menu
                    v-model="shippingHoldDatePickerOpen"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        dense
                        v-model="shippingHoldDate"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        :rules="shippingHoldDateRules"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="shippingHoldDate"
                      :allowed-dates="allowedShippingHoldDates"
                      @input="shippingHoldDatePickerOpen = false"
                    ></v-date-picker>
                  </v-menu>
                  <v-alert
                    dense
                    type="info"
                    color="grey lighten-3"
                    class="mb-2 black--text"
                  >
                    Your order will ship on or after the date selected above.
                  </v-alert>
                </template>
              </template>
              <div class="font-weight-bold pa-0 pt-2">
                Your Reference Number
              </div>
              <v-text-field
                dense
                :placeholder="getCustomerReferencePlaceholder(buySell)"
                :rules="otherRefRules()"
                :id="dynamicID()"
                v-model="customerReference"
              ></v-text-field>
              <template
                v-if="
                  getSelectedCompany.contactList &&
                  getSelectedTradingRole === TradingRoles.SALES_MANAGER &&
                  generateRetailTicket
                "
              >
                <div class="font-weight-bold pa-0 pt-2">
                  Sales Rep (Optional)
                </div>
                <v-select
                  dense
                  v-model="salesRep"
                  :items="sortedContactList()"
                  :item-text="renderSalesRep"
                  item-value="email"
                  label="Sales Rep"
                  single-line
                >
                </v-select>
              </template>
              <trade-execution
                :buy-sell="buySell"
                :company-id="getSelectedCompany.id"
                :total="cartTotal"
                v-on:close="close()"
                v-on:executeTrade="executeTrade()"
                v-on:error-state="handleTradeExecutionError"
                v-if="
                  Object.values(requiredComponentsLoaded).filter((c) => !c)
                    .length === 0
                "
              ></trade-execution>
              <v-progress-linear
                class="mt-4 mb-4"
                indeterminate
                v-else
              ></v-progress-linear>
            </v-stepper-content>
            <v-stepper-content step="2">
              <banking-info
                v-if="step2BankInfo"
                :snown="step2BankInfo"
                :default-tran-type="
                  buySell === 'buy'
                    ? BankTranTypes.DEBIT
                    : BankTranTypes.DEPOSIT
                "
                v-on:close="detailsStep = 1"
                v-on:save="bankInfoAdded()"
              ></banking-info>
              <address-form
                v-else
                :selected-address="blankAddress"
                hide-default-shipping-checkbox="true"
                add-to-address-book-option="true"
                v-on:close="detailsStep = 1"
                v-on:save="newAddressSaved()"
              ></address-form>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import alphanumeric from '@/directives/alphanumeric'
import productMixin from '@/mixins/product'
import orderMixin from '@/mixins/order'
import guardsMixin from '@/mixins/guards'
import validationMixin from '@/mixins/validation'
import {mapGetters} from 'vuex'
import AddressForm from '@/components/company/AddressForm'
import Util from '@/util'
import {FulfillmentTypes, TradingRoles, BankTranTypes, MAX_SHIPPING_HOLD_DAYS, BUSINESS_TIMEZONE} from '@/util/constants'
import {DateTime} from 'luxon-business-days'
import _ from 'lodash'
import WholesaleTicket from '@/components/cart/WholesaleTicket'
import RetailTicket from '@/components/cart/RetailTicket'
import PaymentMethod from '@/components/cart/PaymentMethod'
import ShippingInstructions from '@/components/cart/ShippingInstructions'
import ShippingAddress from '@/components/cart/ShippingAddress'
import TradeExecution from '@/components/cart/TradeExecution'
import BankingInfo from '@/components/company/BankingInfo'
import ChromeAutofillHoneypot from '@/components/widgets/ChromeAutofillHoneypot'

const blankAddress = {};

export default {
  components: {
    ChromeAutofillHoneypot,
    BankingInfo,
    TradeExecution,
    ShippingInstructions,
    ShippingAddress,
    PaymentMethod,
    RetailTicket,
    WholesaleTicket,
    AddressForm,
  },
  props: ['shown', 'buySell'],
  watch: {
    shown: {
      async handler(val) {
        if (val) await this.init()
      },
    },
  },
  directives: {alphanumeric},
  mixins: [productMixin, orderMixin, guardsMixin, validationMixin],
  async mounted() {
    if (this.getSelectedTradingRole === TradingRoles.SALES_REP)
      this.generateRetailTicket = true
    await this.init()
  },
  data() {
    return {
      BankTranTypes,
      get: _.get,
      step2BankInfo: true,
      hasTradeExecutionError: false,
      shippingHoldDatePickerOpen: false,
      MAX_SHIPPING_HOLD_DAYS,
      requiredComponentsLoaded:
        this.buySell === `buy`
          ? {
            shippingInstructions: false,
            shippingAddress: false,
            paymentMethod: false,
          }
          : {
            shippingAddress: false,
            paymentMethod: false,
          },
      wholesaleTicketIndex: 0,
      blankAddress,
      retailTicketIndex: 1,
      confirmationNumber: null,
      initialized: false,
      formValid: null,
      detailsStep: 1,
      addNewAddressType: null,
      validate: false,
      FulfillmentTypes,
      TradingRoles,
      equityTrustVerified: false,
      verifyEquityTrustLoading: false,
      equityTrustErrorMessage: null,
      cartTotal: null,
    }
  },
  computed: {
    ...mapGetters([
      'getSelectedCompany',
      'getDepositoryLocations',
      'getRecentAddress',
      'getBuyCart',
      'getSellCart',
      'getSelectedTradingRole',
      'getUserProfile',
      'getConfig',
    ]),
    processingDelayMessage() {
      return this.getSellCart.processingDelayMessage || null
    },
    generateRetailTicket: {
      get() {
        return this.$store.getters['getCartField'](
          `${this.buySell}.generateRetailTicket`
        )
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.generateRetailTicket`,
          value,
        })
      },
    },
    selectedShippingAddress: {
      get() {
        return this.$store.getters['getCartField'](
          `${this.buySell}.selectedShippingAddress`
        )
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.selectedShippingAddress`,
          value,
        })
      },
    },
    standardDepoAttnName: {
      get() {
        return this.$store.getters['getCartField'](
          `${this.buySell}.standardDepoAttnName`
        )
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.standardDepoAttnName`,
          value,
        })
      },
    },
    standardDepoAccountNumber: {
      get() {
        return this.$store.getters['getCartField'](
          `${this.buySell}.standardDepoAccountNumber`
        )
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.standardDepoAccountNumber`,
          value,
        })
      },
    },
    selectedDepoAddress: {
      get() {
        return this.$store.getters['getCartField'](
          `${this.buySell}.selectedDepoAddress`
        )
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.selectedDepoAddress`,
          value,
        })
      },
    },
    orderNotes: {
      get() {
        return this.$store.getters['getCartField'](`${this.buySell}.orderNotes`)
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.orderNotes`,
          value,
        })
      },
    },
    customerReference: {
      get() {
        return this.$store.getters['getCartField'](
          `${this.buySell}.customerReference`
        )
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.customerReference`,
          value,
        })
      },
    },
    equityTrustAccountNumber: {
      get() {
        return this.$store.getters['getCartField'](
          `${this.buySell}.equityTrustAccountNumber`
        )
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.equityTrustAccountNumber`,
          value,
        })
      },
    },
    equityTrustLinkedAccount: {
      get() {
        const val = this.$store.getters['getCartField'](
          `${this.buySell}.equityTrustLinkedAccount`
        )
        return val
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.equityTrustLinkedAccount`,
          value,
        })
      },
    },
    fulfillmentType: {
      get() {
        return (
          this.$store.getters['getCartField'](
            `${this.buySell}.fulfillmentType`
          ) || FulfillmentTypes.STANDARD
        )
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.fulfillmentType`,
          value,
        })
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.generateRetailTicket`,
          value: value === FulfillmentTypes.EQUITY_TRUST,
        })
      },
    },
    selectedShippingInstructionId: {
      get() {
        const selectedShippingInstructions = this.$store.getters['getCartField'](`${this.buySell}.selectedShippingInstructions`) || {};
        return parseInt(selectedShippingInstructions.netsuite_internalId) || null;
      }
    },
    retailTotal: {
      get() {
        return this.$store.getters['getCartField'](
          `${this.buySell}.retailTotal`
        )
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.retailTotal`,
          value,
        })
      },
    },
    salesRep: {
      get() {
        return this.$store.getters['getCartField'](`${this.buySell}.salesRep`)
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.salesRep`,
          value,
        })
      },
    },
    shippingHoldEnabled: {
      get() {
        return this.$store.getters['getCartField'](`${this.buySell}.shippingHoldEnabled`) || false
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.shippingHoldEnabled`,
          value,
        })
        // Clear date when disabled
        if (!value) {
          this.$store.commit('updateCartField', {
            path: `${this.buySell}.shippingHoldDate`,
            value: null,
          })
        }
      },
    },
    shippingHoldDate: {
      get() {
        return this.$store.getters['getCartField'](`${this.buySell}.shippingHoldDate`)
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.shippingHoldDate`,
          value,
        })
      },
    },
    shippingHoldDateRules() {
      return [
        (v) => !this.shippingHoldEnabled || !!v || 'Please select a release date',
      ]
    },
    validateShippingAddress() {
      return this.validateShippingAddressComplete(this.buySell, this.selectedShippingInstructionId)
    },
  },
  methods: {
    dynamicID: Util.dynamicID,
    handleTradeExecutionError(hasError) {
      this.hasTradeExecutionError = hasError
    },
    allowedShippingHoldDates(val) {
      // Uses the allowedDates method from orderMixin with an explicit BUSINESS_TIMEZONE
      // refDate so that shipping hold validation matches the server-side check
      // (which always evaluates "today" in Eastern Time).
      const refDate = DateTime.now().setZone(BUSINESS_TIMEZONE).startOf('day')
      return this.allowedDates(val, this.MAX_SHIPPING_HOLD_DAYS, refDate)
    },
    getDepositoryLocationsWrapper() {
      // Hardcoded override logic. Will come up with a better pattern if we find other use cases.
        const depositoryLocations = [...this.getDepositoryLocations].filter(address => {
          if (this.fulfillmentType === FulfillmentTypes.EQUITY_TRUST) {
            return Boolean(address.et_depository_enum)
          } else {
            return Boolean(address.et_only) === false
          }
        })
      if (
        this.getSelectedCompany.netsuite_internalId === this.NGI_NETSUITE_INTERNAL_ID
      )
        return depositoryLocations.map((loc) =>
          Object.assign({}, loc, {
            label: `${loc.label} - (NGI)`,
          })
        )
      return depositoryLocations
    },
    sortedContactList() {
      if (this.getSelectedCompany.contactList.length > 0) {
        return [...this.getSelectedCompany.contactList].sort((a, b) =>
          a.email.toLowerCase().localeCompare(b.email.toLowerCase())
        )
      } else return []
    },
    renderSalesRep(item) {
      const nameParts = [item.firstName, item.lastName]
        .filter((x) => typeof x === `string` && x.length > 0)
        .map((x) => {
          return x.trim()
        })
      return nameParts.length > 0
        ? `${nameParts.join(' ')} (${item.email})`
        : item.email
    },
    showRetailTicket() {
      // Only show retail ticket for sales manager role
      return (
        this.fulfillmentType === FulfillmentTypes.EQUITY_TRUST ||
        this.generateRetailTicket
      )
    },
    updateTotal(result) {
      this.cartTotal = result.total
      if (
        this.getSelectedTradingRole === TradingRoles.SALES_REP &&
        this.generateRetailTicket === false &&
        parseFloat(this.retailTotal) !== parseFloat(result.total)
      )
        this.retailTotal = result.total
      else if (
        this.getSelectedTradingRole !== TradingRoles.SALES_REP &&
        this.generateRetailTicket === false &&
        this.retailTotal !== 0
      )
        this.retailTotal = 0
      if (this.equityTrustVerified)
        this.$refs.equityTrustAccountNumberField.validate()
      if (
        this.isEquityTrustEligible() &&
        typeof this.$refs.fulfillmentTypeField === `object`
      )
        this.$refs.fulfillmentTypeField.validate()
    },
    resetEquityTrust() {
      this.equityTrustVerified = false
      this.equityTrustErrorMessage = null
      this.equityTrustLinkedAccount = {
        ownerContactName: null,
        accountType: null,
        availableCash: 0,
      }
    },
    showEquityTrustLinkedAccountHint() {
      if (
        this.equityTrustErrorMessage ||
        (this.equityTrustVerified &&
          this.equityTrustLinkedAccount.ownerContactName)
      )
        return true
      else return false
    },
    equityTrustLinkedAccountText() {
      return `${this.equityTrustLinkedAccount.ownerContactName} - ${
        this.equityTrustLinkedAccount.accountType
      } - Available Funds: ${this.toCurrency(
        this.equityTrustLinkedAccount.availableCash
      )}`
    },
    equityTrustLinkedAccountHint() {
      if (this.equityTrustErrorMessage)
        return `<span class="red--text">${this.equityTrustErrorMessage}</span>`
      else if (this.equityTrustLinkedAccount.ownerContactName)
        return this.equityTrustLinkedAccountText()
      else return ``
    },
    getCart() {
      return this.buySell === 'buy' ? this.getBuyCart : this.getSellCart
    },
    iraEligibleCart() {
      if (this.buySell === `sell` || !this.isEquityTrustEligible()) return false
      const nonIraEligible = this.getCart().items.filter((x) => {
        return x.item.iraEligible === false
      })
      return nonIraEligible.length === 0
    },
    async executeTrade() {
      // Only returns new confirmation number if one not already set.
      this.confirmationNumber = this.getConfirmationNumber(
        `PG`,
        this.confirmationNumber
      )
      if (
        this.fulfillmentType === FulfillmentTypes.EQUITY_TRUST &&
        !this.equityTrustVerified
      )
        await this.verifyEquityTrust()
      if (!this.$refs.tradeForm.validate()) {
        window._VMA.$emit('SHOW_SNACKBAR', {
          text: 'Please correct the errors before submitting your trade. Please note quantities and/or pricing may have changed due to market conditions.',
          color: 'error',
        })
      } else if (
        this.buySell === 'sell' &&
        _.get(this.getSellCart, 'preSettlementPreview.creditBlocked')
      ) {
        window._VMA.$emit('SHOW_SNACKBAR', {
          text: _.get(
            this.getSellCart,
            'preSettlementPreview.message',
            'Pre-Settlement Payment Program total exceeds available credit.'
          ),
          color: 'error',
        })
      } else {
        this.$emit('executeTrade', this.buySell, this.confirmationNumber)
        this.confirmationNumber = null
      }
    },
    async init() {
      await Promise.all([
        this.$store.dispatch('updateProductsInCart'),
        this.$store.dispatch('refreshSelectedCompany'), //This ensures settings such as waiveHandling are up to date
        this.$store.dispatch('fetchProcessingDelayMessage'),
      ])
      if (this.buySell === `buy`) {
        this.getCart().waiveHandling = Boolean(this.getSelectedCompany.waiveHandling)
        if (
          this.fulfillmentType === FulfillmentTypes.EQUITY_TRUST ||
          this.selectedShippingInstructionId !== this.DEPOSITORY_INSTRUCTIONS_NS_ID
        ) {
          // these components are not required for equity trust, so mark them loaded
          this.requiredComponentsLoaded.shippingInstructions = true
          this.requiredComponentsLoaded.shippingAddress = true
        }
      }
      if (this.buySell === `sell`) {
        // map company.waivePoHandling onto cart's generic waiveHandling flag
        this.getCart().waiveHandling = Boolean(this.getSelectedCompany.waivePoHandling)
      }
      if (!this.initialized) {
        await this.$store.dispatch('fetchDepositoryLocations')
        this.salesRep = this.getUserProfile.email
        this.initialized = true
      } else {
        if (
          this.$refs.tradeForm !== undefined &&
          !this.$refs.tradeForm.validate()
        ) {
          window._VMA.$emit('SHOW_SNACKBAR', {
            text: 'Please correct the errors before submitting your trade. Please note quantities and/or pricing may have changed due to market conditions.',
            color: 'error',
          })
        }
      }
    },
    async verifyEquityTrust() {
      this.verifyEquityTrustLoading = true
      this.equityTrustErrorMessage = null
      try {
        await this.$store.dispatch(
          'linkEquityTrustAccount',
          this.getCart().equityTrustAccountNumber
        )
        this.equityTrustVerified = true
        this.$refs.equityTrustAccountNumberField.validate()
      } catch (e) {
        this.equityTrustErrorMessage = _.get(e, `response.data`, e.message)
      } finally {
        this.verifyEquityTrustLoading = false
      }
    },
    addBankingInfo() {
      this.step2BankInfo = true
      this.detailsStep = 2
    },
    addNewAddress(type) {
      this.step2BankInfo = false
      this.addNewAddressType = type
      this.detailsStep = 2
    },
    async bankInfoAdded() {
      this.detailsStep = 1
      await this.$refs.paymentMethod.init()
    },
    async newAddressSaved() {
      await this.$store.dispatch('fetchSelectedCompanyAddresses', {
        page: 1,
        itemsPerPage: 10000,
      })
      this.selectedShippingAddress = this.getRecentAddress
      this.detailsStep = 1
    },
    close() {
      this.$emit('close')
    },
    otherRefRules() {
      return [
        ...this.characterLimit(35),
        (v) => {
          return (
            v === undefined ||
            !!v ||
            !this.showRetailTicket() ||
            'This field is required for retail tickets. Use the unique identifier your customers will recognize.'
          )
        },
      ]
    },
    getRulesForEquityTrust() {
      return [
        (v) => {
          return (
            v === undefined ||
            !!v ||
            this.fulfillmentType === FulfillmentTypes.STANDARD ||
            'This field is required'
          )
        },
        () =>
          (this.equityTrustVerified && !this.equityTrustErrorMessage) ||
          this.equityTrustErrorMessage === null ||
          `${this.equityTrustErrorMessage}`,
        () =>
          (this.equityTrustVerified &&
            this.equityTrustLinkedAccount.availableCash > this.retailTotal) ||
          !this.equityTrustVerified ||
          `${this.equityTrustLinkedAccountText()} - INSUFFICIENT FUNDS`,
      ]
    },
    fulfillmentTypeRules() {
      return [
        (v) => {
          return v === undefined || v.id !== null || 'This field is required'
        },
        () => {
          return (
            this.fulfillmentType === FulfillmentTypes.STANDARD ||
            (this.fulfillmentType === FulfillmentTypes.EQUITY_TRUST &&
              this.iraEligibleCart()) ||
            'DirectLink IRA™ for Equity Trust is only available if all items in cart are IRA eligible.'
          )
        },
      ]
    },
    swapWholesaleRetailTicketOrder() {
      const w = this.wholesaleTicketIndex
      const r = this.retailTicketIndex
      this.retailTicketIndex = w
      this.wholesaleTicketIndex = r
    },
  },
}
</script>

<style>
.cartItems table {
  border: 1px solid #eaebed;
}

.cartItems table thead {
  background-color: #f7f8fa;
  color: #888e99;
  text-transform: uppercase;
}

.trade-stepper {
  width: 100%;
}

@media (max-width: 600px) {
  .trade-stepper {
    padding: 0 !important;
  }
  
  .v-text-field {
    margin-bottom: 8px;
  }
  
  .v-select {
    margin-bottom: 8px;
  }
  
  .v-stepper__content {
    padding: 8px !important;
  }
  
  .v-stepper__items {
    padding: 0 !important;
  }
  
  .v-stepper__wrapper {
    padding: 0 !important;
  }
  
  .v-stepper__header {
    padding: 0 !important;
  }
  
  .v-stepper__step {
    padding: 8px !important;
  }
  
  .v-stepper__step__step {
    margin-right: 8px !important;
  }
  
  .v-stepper__step__title {
    font-size: 0.875rem !important;
  }
}


@media (max-width: 600px) {
  .no-gutters-xs {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
}
</style>
